import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tag } from "@digital-twins/design-system/icons";
import type { ResolvedStudyBrand } from "@digital-twins/shared-types";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface StudyBrandChipProps {
  /** The resolved study brand (`resolveStudyBrand`), or null for none. */
  brand: ResolvedStudyBrand | null;
  /**
   * Saves the user's edit (`set_study_brand`); `null` is an explicit "no
   * brand". Resolves whether it was saved.
   */
  onSave: (brandName: string | null) => Promise<boolean>;
  /** Locks the chip while anything writes the draft. */
  disabled?: boolean;
}

/**
 * The "Study brand" setting (plan Decision 5), beside the language chip:
 * inferred from the objective or the organization, editable, optional.
 */
export function StudyBrandChip({ brand, onSave, disabled }: StudyBrandChipProps) {
  const { t } = useTranslation("app");
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(brand?.brandName ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setValue(brand?.brandName ?? "");
  }, [open, brand?.brandName]);

  const save = async (brandName: string | null) => {
    setSaving(true);
    try {
      if (await onSave(brandName)) setOpen(false);
    } finally {
      setSaving(false);
    }
  };
  const trimmed = value.trim();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="study-brand-chip"
          aria-label={t("questionnaire.studyBrand.label")}
          title={t("questionnaire.studyBrand.label")}
          disabled={disabled}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm transition-colors",
            "hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
            brand ? "text-foreground" : "text-muted-foreground",
          )}
        >
          <Tag className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="whitespace-nowrap leading-none">
            {brand?.brandName ?? t("questionnaire.studyBrand.none")}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3">
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (trimmed) void save(trimmed);
          }}
        >
          <label
            htmlFor="study-brand-input"
            className="text-sm font-medium text-foreground"
          >
            {t("questionnaire.studyBrand.label")}
          </label>
          <p className="text-[13px] leading-[1.45] text-muted-foreground">
            {t("questionnaire.studyBrand.help")}
          </p>
          <Input
            id="study-brand-input"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={t("questionnaire.studyBrand.placeholder")}
            disabled={saving}
            data-testid="study-brand-input"
          />
          {brand ? (
            <p
              className="text-[12px] text-muted-foreground"
              data-testid="study-brand-source"
            >
              {t(`questionnaire.studyBrand.source.${brand.source}`)}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={saving || !brand}
              onClick={() => void save(null)}
              data-testid="study-brand-clear"
            >
              {t("questionnaire.studyBrand.clear")}
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={saving || !trimmed || trimmed === brand?.brandName}
              data-testid="study-brand-save"
            >
              {t("questionnaire.studyBrand.save")}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
